/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
/// REACT ///
import { useEffect } from "react";
import { useState } from "react";
/// HANDLERS ///
import { handleUpdateCounter } from "handler/counter/update";
/// TYPES ///
import { Counter } from "api/types/sketch";
import { Corner } from "api/types/sketch";
import { CornerError } from "api/types/quote/error";
import { IHistoryActionType } from "api/types/history";
import { Slab } from "api/types/quote";
/// ACTIONS ///
import { updateLocalCounter } from "api/actions/sketch/counter";
import { restore } from "api/actions/history";
import { replaceRestorePoint } from "api/actions/history";
/// COMPONENTS ///
import UpdateEdge from "components/quote/drawer/update_edge";
/// FUNCTIONS ///
import { updateCorner } from "functions/sketch";
import { getFirstSlabInAreas } from "functions/sketch/get/slab";
/// VALUES ///
import { EASED } from "values/values";
import { FINISHED } from "values/values";
import { MITER } from "values/values";
import { SPLASH } from "values/values";
import { WATERFALL } from "values/values";

interface Props {
  open: boolean,
  setOpen: (open: boolean) => void,
  original_corner: Corner | null,
  selected_counter: Counter | null
}

export default function UpdateStoreEdge({ open, setOpen, original_corner, selected_counter }: Props) {
  const dispatch = useAppDispatch();
  const { areas } = useAppSelector((state) => state.quote);
  const { cut_shapes } = useAppSelector((state) => state.sketch);
  const [corner, setCorner] = useState<Corner>({});
  const [error, setError] = useState<CornerError>({});

  useEffect(() => {
    if (open) {
      setCorner(original_corner);
      dispatch(replaceRestorePoint(
        {
          undo_action: IHistoryActionType.UPDATE_COUNTER,
          desc: "Cancel Update Edge",
          undo_data: { ...selected_counter }
        }
      ));
    }
  }, [open, original_corner]);

  const cancel = () => {
    dispatch(restore());
    setOpen(false);
  }

  const save = () => {
    if (corner?.edge_type === SPLASH || corner?.edge_type === MITER || corner?.edge_type === WATERFALL) {
      if (corner?.splash_height < 0) {
        setError({ ...error, height_error: "Height must be 0 or greater", height_has_error: true });
        return;
      }
    }

    if (selected_counter) {
      const counter: Counter = updateCorner(selected_counter, corner);
      const slab: Slab | null = getFirstSlabInAreas(areas, counter.area_uuid);
      handleUpdateCounter(
        dispatch,
        { ...selected_counter },
        counter,
        cut_shapes.filter(shape => shape.counter_uuid === selected_counter.uuid),
        slab.uuid,
        "Change Counter Edge"
      );
    }
    setOpen(false);
  }

  const update = (corner: Corner) => {
    const counter: Counter = updateCorner(selected_counter, corner);
    if (counter) {
      dispatch(updateLocalCounter(counter));
    }
    setCorner(corner);
  }

  return (
    <UpdateEdge
      open={open}
      edge_type={corner?.edge_type ?? FINISHED}
      setEdgeType={edge_type => update({ ...corner, edge_type })}
      finished_edge_type={corner?.finished_edge_type ?? EASED}
      setFinishedEdgeType={finished_edge_type => update({ ...corner, finished_edge_type })}
      splash_height={corner?.splash_height ?? 0}
      setSplashHeight={splash_height => update({ ...corner, splash_height })}
      height_error={error?.height_error}
      height_has_error={error?.height_has_error}
      save={save}
      cancel={cancel} />
  );
}
